import { getErrorMessage } from "api/errors";
import { useEvents } from "api/events";
import {
  hasAnyAccessToResource,
  hasModifyAccessToResource,
  hasViewAccessToResource,
} from "api/general";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ImportJsonModal } from "components/ImportJsonModal/ImportJsonModal";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useTableSelectable } from "components/Table/useTableSelectable";
import { TargetTenantModal } from "components/TargetTenantModal/TargetTenantModal";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setImportedCustomEventSchemas } from "reduxState/customEventSchemas/customEventSchemas";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { CUSTOM_EVENTS_SCHEMA_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { exportToJsonFile } from "utilities/exportToJson";
import { links } from "utilities/links";
import { handleBooleanValue } from "utilities/strings";
import { prepareExportCustomEventSchemasJson } from "../utils";
import { AttributeChip } from "./AttributeChips";

enum ModalMode {
  Activate,
  Deactivate,
  Import,
  DifferentTenant,
}

interface Modal {
  eventType: string | null;
  mode: number | null;
  isOpen: boolean;
}

export function CustomEventsSchemaList() {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { listing, updateListing } = useListing(CUSTOM_EVENTS_SCHEMA_LIST_ID);
  const appDispatch = useAppDispatch();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[CUSTOM_EVENTS_SCHEMA_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      CUSTOM_EVENTS_SCHEMA_LIST_ID,
    );

    setFilters(filters);
  };
  const {
    rowsSelected,
    selectRow,
    unselectRow,
    selectAll,
    unselectAll,
    disabled,
    clearSelection,
  } = useTableSelectable();
  const customEventSchemasExportData = prepareExportCustomEventSchemasJson(
    rowsSelected.map((row) => row.data),
  );

  const { useListOfCustomEventsSchema, activateSchema } = useEvents();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { data: listOfCustomEvents, error } = useListOfCustomEventsSchema({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const [modal, setModal] = useState<Modal>({
    eventType: null,
    mode: null,
    isOpen: false,
  });
  const [isSelectable, setIsSelectable] = useState(false);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "eventName" }),
      property: "name",
      testId: "custom-events-header-event-name",
      isSortable: true,
      customWidth: "250px",
    },
    {
      headerText: message({ id: "eventSystemIdentifier" }),
      property: "eventType",
      testId: "custom-events-header-event-system-identifier",
      isSortable: true,
      customWidth: "250px",
    },
    {
      headerText: message({ id: "createDate" }),
      property: "createdAt",
      testId: "custom-events-header-create-date",
      isSortable: true,
    },
    {
      headerText: message({ id: "active" }),
      property: "active",
      testId: "custom-events-header-active",
      isSortable: true,
    },
    {
      headerText: message({ id: "attributes" }),
      testId: "custom-events-header-attributes",
      property: "attributes",
      isSortable: false,
    },
  ]);

  const createItems = (items: any[]): Row[] => {
    return items.map((item) => ({
      cells: [
        { content: item.name, property: "name", maxWidth: "200px" },
        { content: item.eventType, property: "eventType", maxWidth: "200px" },
        { content: OLDate(item.createdAt), property: "createdAt" },
        {
          content: message({ id: handleBooleanValue(item.active) }),
          property: "active",
        },
        {
          content: item.schema?.fields?.length ? (
            <AttributeChip items={item.schema.fields} key={item.name} />
          ) : (
            "-"
          ),
          property: "attributes",
        },
      ],
      id: item.eventType,
      data: item,
      metadata: { disabled: !item.active },
      testId: "custom-events-table-row",
    }));
  };

  const items = listOfCustomEvents?.items
    ? createItems(listOfCustomEvents.items)
    : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && listOfCustomEvents) {
      setPersistentFilters(filters, CUSTOM_EVENTS_SCHEMA_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, listOfCustomEvents, error]);

  const hasModifyCustomEventPermission =
    hasModifyAccessToResource("CUSTOM_EVENT");
  const hasViewCustomEventPermission = hasViewAccessToResource("CUSTOM_EVENT");
  const hasAnyCustomEventPermission = hasAnyAccessToResource("CUSTOM_EVENT");

  return (
    <>
      <TopHeader
        title={message({ id: "customEventSchemas" })}
        clearLink={
          hasModifyCustomEventPermission ? links.customEventsList : undefined
        }
      />
      <Subheader>
        <FiltersV2
          data-test-id="custom-events"
          donutLabel={message({ id: "schemas" })}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "active",
              label: message({ id: "active" }),
              type: "boolean-status",
            },
            {
              query: "createdAt",
              label: message({ id: "createDate" }),
              type: "datetime",
            },
            {
              query: "name",
              label: message({ id: "eventName" }),
              type: "event-name-filter",
            },
            {
              query: "eventType",
              label: message({ id: "eventSystemIdentifier" }),
              type: "event-system-identifier-filter",
            },
          ]}
          setFilters={setSelectedFilters}
          total={listOfCustomEvents?.total}
        />
        {hasAnyCustomEventPermission ? (
          <SubheaderMenuButton
            text={message({ id: "addEventSchema" })}
            menuItems={[
              {
                onClick: () => navigate(links.addCustomEventSchema),
                text: message({ id: "addEventSchema" }),
              },
              {
                onClick: () =>
                  setModal({
                    eventType: null,
                    mode: ModalMode.Import,
                    isOpen: true,
                  }),
                text: message({ id: "importEventSchemas" }),
              },
            ]}
            disabled={hasViewCustomEventPermission}
            data-test-id="custom-events"
          />
        ) : null}
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          {isSelectable ? (
            <>
              <SubheaderActionButton
                text={message({ id: "close" }).toUpperCase()}
                onClick={() => setIsSelectable(false)}
              />
              <SubheaderMenuButton
                text={`${message({ id: "bulkActions" }).toUpperCase()}... (${rowsSelected.length})`}
                menuItems={[
                  {
                    text: message({ id: "downloadJsonFile" }),
                    onClick: () =>
                      exportToJsonFile({
                        data: customEventSchemasExportData,
                        name: "customEventSchemas",
                        callback: () => {
                          clearSelection();
                          return setIsSelectable(false);
                        },
                      }),
                  },
                  {
                    text: message({ id: "copyToDifferentTenant" }),
                    onClick: () =>
                      setModal({
                        eventType: null,
                        mode: ModalMode.DifferentTenant,
                        isOpen: true,
                      }),
                  },
                ]}
                hideArrow
                disabled={rowsSelected.length === 0}
                data-test-id="table-actions"
              />
            </>
          ) : (
            <SubheaderActionButton
              text={message({ id: "bulkActions" }).toUpperCase()}
              onClick={() => setIsSelectable(true)}
              data-test-id="select-to-export"
            />
          )}
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            properties={[
              {
                id: "name",
                label: "name",
              },
              { id: "eventType", label: "eventSystemIdentifier" },
            ]}
            onSearch={() =>
              appDispatch(
                clearTablePage({ tableName: CUSTOM_EVENTS_SCHEMA_LIST_ID }),
              )
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={CUSTOM_EVENTS_SCHEMA_LIST_ID}
        data-test-id="custom-events"
        onRowClick={(id: string) => {
          navigate(links.editCustomEventSchema(id as string));
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  eventType: id as string,
                  mode: ModalMode.Activate,
                  isOpen: true,
                });
              },
              disableCondition: () => hasViewCustomEventPermission,
              hideCondition: (item: Row) => !item.metadata?.disabled,
              label: message({ id: "activateSchema" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  eventType: id as string,
                  mode: ModalMode.Deactivate,
                  isOpen: true,
                });
              },
              disableCondition: () => hasViewCustomEventPermission,
              hideCondition: (item: Row) => item.metadata?.disabled,
              label: message({ id: "deactivateSchema" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editCustomEventSchema(`${id}`)),
              disableCondition: () => hasViewCustomEventPermission,
              label: message({ id: "edit" }),
            },
          ],
        }}
        columns={listOfCustomEvents && headItems}
        rows={listOfCustomEvents && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            listOfCustomEvents?.total.filtered,
            listOfCustomEvents?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        selectable={
          isSelectable
            ? {
                rowsSelected,
                selectRow,
                unselectRow,
                selectAll,
                unselectAll,
                disabled,
                clearSelection,
              }
            : undefined
        }
        clearFilters={() => setFilters([])}
      />
      {modal?.mode === ModalMode.Activate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activateSchema(
                modal.eventType as string,
                true,
              );
              if (response) {
                setModal({ eventType: null, mode: null, isOpen: false });
                notify(message({ id: "schemaActivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
            }
          }}
          close={() => setModal({ eventType: null, mode: null, isOpen: false })}
          header="thisSchemaWillBeActivated"
          info="activateSchemaInfo"
          open={modal.isOpen}
          data-test-id="event-schema-status-confirm"
          modalWidth={454}
        />
      ) : modal?.mode === ModalMode.Deactivate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activateSchema(
                modal.eventType as string,
                false,
              );
              if (response) {
                setModal({ eventType: null, mode: null, isOpen: false });
                notify(message({ id: "schemaDeactivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
            }
          }}
          close={() => setModal({ eventType: null, mode: null, isOpen: false })}
          header="thisSchemaWillBeDeactivated"
          info="deactivateSchemaInfo"
          open={modal.isOpen}
          data-test-id="event-schema-status-confirm"
          modalWidth={454}
        />
      ) : modal?.mode === ModalMode.Import ? (
        <ImportJsonModal
          headerMessage="importEventSchemas"
          isOpen={modal.isOpen}
          onClose={() =>
            setModal({ eventType: null, mode: null, isOpen: false })
          }
          importTooBigErrorMessage="maxImportEventSchemaCountReached"
          setJsonAction={setImportedCustomEventSchemas}
          link={links.customEventSchemaImportList}
        />
      ) : modal?.mode === ModalMode.DifferentTenant ? (
        <TargetTenantModal
          isOpen={modal.isOpen}
          headerText={message(
            {
              id:
                rowsSelected.length === 1
                  ? "copyOneCustomEventSchemaToTargetTenant"
                  : "copyCustomEventSchemasToTargetTenant",
            },
            {
              count: rowsSelected.length,
            },
          )}
          onClose={() =>
            setModal({ eventType: null, mode: null, isOpen: false })
          }
          setJsonAction={setImportedCustomEventSchemas}
          data={customEventSchemasExportData}
          link={links.customEventSchemaImportList}
          infoText={message({ id: "copiedCustomEventSchemasInfo" })}
          buttonText={message({
            id:
              rowsSelected.length === 1
                ? "copyCustomEventSchemaAndViewTargetTenant"
                : "copyCustomEventSchemasAndViewTargetTenant",
          })}
        />
      ) : null}
    </>
  );
}
